<template>
  <div class="print-risk-analyses-page">
    <div class="loading" v-if="loading">
      <div class="loading-panel">
        <ProgressSpinner />
      </div>
    </div>

    <div class="row mb-3" v-if="risks?.length > 0">
      <div class="col-12 d-flex">
        <label class="mr-2">
          {{ risks?.length == 1 ? 'Categoria de risco selecionada' : 'Categorias de risco selecionadas' }}:
        </label>
        <div v-for="risk in risks" :key="risk" class="mr-2">
          <div
            class="risk-category"
            :class="{ unacceptable: risk === 8 }"
            :style="`background-color: ${getHrnLevelColor(risk)}`"
          >
            {{ getRiskCategoryLabel(risk) }}
          </div>
        </div>
      </div>
    </div>

    <div class="risk-analyses-list" v-if="!loading">
      <div class="row">
        <div class="col col-12">
          <div class="paginator" v-if="totalRecords > perPage">
            <Paginator
              :first="(currentPage - 1) * perPage"
              :rows="perPage"
              :total-records="totalRecords"
              @page="onPage($event)"
            ></Paginator>
          </div>
        </div>

        <div class="col col-12 no-print">
          <div class="my-2">
            <router-link
              :to="{ name: 'preliminary-risk-analyses' }"
              class="btn btn-success"
            >
                <font-awesome-icon :icon="['fas', 'arrow-left']"></font-awesome-icon>
                Voltar para AR
            </router-link>
            <button
              class="btn btn-primary ml-3"
              :class="{ 'btn-loading': loadingPrint }"
              :disabled="loadingPrint"
              @click="print"
            >
              <span v-if="loadingPrint"
                ><ProgressSpinner
                  style="width: 18px; height: 18px"
                  stroke-width="5"
              /></span>
              <span v-if="!loadingPrint">
                <font-awesome-icon :icon="['fas', 'print']"></font-awesome-icon>
                Salvar / Imprimir
              </span>
            </button>
          </div>
        </div>
      </div>

      <div
        id="printable-data"
        class="printable-data"
        v-for="machine in machines"
        :key="machine.id"
      >
        <div class="row mb-3 prevent-print-cut">
          <div class="col-12">
            <machine-header
              :machine="machine"
              :prepare-for-print="true"
            ></machine-header>
          </div>
        </div>

        <div
          class="row machine-inventory prevent-print-cut break-after"
          v-if="!isCompanyMosaic()"
        >
          <div class="col-12">
            <machine-inventory-table
              :machine="machine"
              :safety-systems="safety_systems"
              :prepare-for-print="true"
            ></machine-inventory-table>
          </div>
        </div>

        <div class="row machine-aprs mt-3 break-after">
          <div class="col-12">
            <machine-apr-list
              :machine-aprs="machine.riskAnalyses"
              :prepare-for-print="true"
            ></machine-apr-list>
          </div>
        </div>

        <div class="bottom" ref="bottom"></div>
      </div>

      <div class="not-found" v-if="!machines || machines.length == 0">
        Nenhum registro encontrado...
      </div>
    </div>
  </div>
</template>

<script>
  import { computed } from '@vue/runtime-core'
  import { useRoute, useRouter } from 'vue-router'
  import { useStore } from 'vuex'

  import Paginator from 'primevue/paginator'
  import ProgressSpinner from 'primevue/progressspinner'

  import MachineAprList from './components/MachineAprList.vue'
  import MachineHeader from './components/MachineHeader.vue'
  import MachineInventoryTable from './components/MachineInventoryTable.vue'

  import useHrn from '@/modules/Hrn'
  import { MOSAIC_FERTILIZANTES_ID } from '@/constants/config.constants.js'

  export default {
    components: {
      Paginator,
      ProgressSpinner,
      MachineHeader,
      MachineInventoryTable,
      MachineAprList,
    },
    setup() {
      const route = useRoute()
      const router = useRouter()
      const store = useStore()
      const selected_company = computed(() => store.getters.selected_company)
      const selected_order_of_service = computed(() => store.getters.selected_order_of_service)
      let { filters } = route.params

      try {
        filters = JSON.parse(filters)
        console.log({ filters })
      } catch (ex) {
        console.log('Error parsing printing filters...')
        filters = {}
      }

      const { getHrnLevelColor } = useHrn()

      const adequacy_status = [
        { label: 'Conforme', value: 0 },
        { label: 'Aceitável', value: 1 },
        { label: 'Muito Baixo', value: 2 },
        { label: 'Baixo', value: 3 },
        { label: 'Significante', value: 4 },
        { label: 'Alto', value: 5 },
        { label: 'Muito Alto', value: 6 },
        { label: 'Extremo', value: 7 },
        { label: 'Inaceitável', value: 8 },
      ]

      const risks = filters.show_only_selected_risk_categories ? filters.adequacy_status : []

      return {
        selected_company,
        selected_order_of_service,
        machineId: route?.params?.machineId,
        router,
        filters,
        getHrnLevelColor,
        adequacy_status,
        risks,
      }
    },
    data() {
      return {
        machines: [],
        safety_systems: [],
        loading: false,
        loadingPrint: false,
        currentPage: 1,
        perPage: 0,
        totalRecords: 0,
      }
    },
    methods: {
      print() {
        if (this.loadingPrint) return

        this.loadingPrint = true

        Promise.all(
          Array.from(document.images)
            .filter((img) => !img.complete)
            .map(
              (img) =>
                new Promise((resolve) => {
                  img.onload = img.onerror = resolve
                }),
            ),
        ).then(() => {
          console.log('images finished loading')
          this.loadingPrint = false
          window.print()
        })
      },
      load() {
        var app = this
        this.loading = true

        app.axios
          .get('/api/safety_systems/all')
          .then(function (response) {
            app.safety_systems = response.data.data
          })
          .catch(function (error) {
            console.log(error)
          })

        this.loadData()
      },
      async loadData() {
        var app = this
        this.loading = true

        const queryString = this.getQueryString(this.currentPage, this.perPage)

        let machines = await app.axios
          .get(
            `/api/printing/print-risk-analyses?order_of_service_id=${this.selected_order_of_service.id}${queryString}`,
          )
          .then(({ data }) => {
            app.perPage = data.perPage
            app.totalRecords = data.totalRecords

            return data.data
          })
          .catch((error) => {
            console.log(error)
            return []
          })
          .finally(() => {
            app.loading = false
          })

        if (this.risks?.length > 0) {
          for (const key in machines) {
            machines[key].riskAnalyses = machines[key].riskAnalyses.filter(rad => {
              return this.risks.includes(rad.calculated_hrn_level)
            })
          }
        }
        
        app.machines = machines
      },
      getQueryString(page, perPage) {
        let query = ''

        if (page) query += `&page=${page}`

        if (perPage) query += `&perPage=${perPage}`

        if (this.filters) {
          const queryStringFilters = Object.keys(this.filters)
            .filter((key) => this.filters[key])
            .map((key) => key + '=' + this.filters[key])
            .join('&')

          query += `&${queryStringFilters}`
        }

        return query
      },
      onPage(event) {
        const page = event.page + 1
        this.currentPage = page

        this.loadData()
      },
      getRiskCategoryLabel(risk) {
        return this.adequacy_status.filter(status => status.value == risk)[0].label
      },
      isCompanyMosaic() {
        return this.selected_company.id == MOSAIC_FERTILIZANTES_ID
      },
    },
    mounted() {
      this.load()
    },
  }
</script>

<style lang="scss">
  .loading {
    position: absolute;
    top: 0;
    width: 100vw;
    left: 0;
    height: 100vh;

    .loading-panel {
      position: absolute;
      top: 0;
      left: 0;

      height: 100%;
      width: 100%;

      background-color: #e9ecef;

      display: flex;
      align-items: center;
      justify-content: center;

      opacity: 0.9;
    }
  }

  .btn-print {
    min-width: 100px;

    &.btn-loading {
      pointer-events: none;
    }
  }

  @media print {
    // .machine-apr-list {
    //   &__item prevent-print-cut {
    //     break-inside: avoid;
    //   }
    // }

    .loading {
      display: none;
    }

    .prevent-print-cut {
      break-inside: avoid;
    }

    .break-after {
      page-break-after: always;
    }
  }

  .risk-category {
    padding: 2px 10px;
    border-radius: 15px;
  }

  .unacceptable {
    color: white;
  }
</style>
